import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PROJECT_STATUSES } from "@/lib/projectStatus";
import { Home } from "lucide-react";
import { toast } from "sonner";

export default function ProjectCreationModal({ open, onOpenChange, onCreate }) {
  const [form, setForm] = useState({ name: "", location: "", budget: "", surface: "", description: "", status: "en_gestion" });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !form.location.trim()) {
      toast.error("Indica al menos el nombre y el municipio de tu vivienda.");
      return;
    }
    setSaving(true);
    try {
      await onCreate({
        name: form.name.trim(),
        location: form.location.trim(),
        budget: form.budget ? Number(form.budget) : null,
        surface: form.surface ? Number(form.surface) : null,
        description: form.description.trim() || null,
        status: form.status,
      });
      setForm({ name: "", location: "", budget: "", surface: "", description: "", status: "en_gestion" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-lg ${onOpenChange ? "" : "[&>button]:hidden"}`} data-testid="project-creation-modal">
        <DialogHeader>
          <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-2">
            <Home className="h-5 w-5" />
          </div>
          <DialogTitle className="text-2xl">Nuevo proyecto de vivienda</DialogTitle>
          <DialogDescription>
            Lo básico de la casa. Podrás editar los detalles y añadir usuarios después.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="pname">Nombre de la vivienda *</Label>
            <Input
              id="pname"
              data-testid="project-name-input"
              placeholder="Ej. Casa de los olivos"
              value={form.name}
              onChange={set("name")}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ploc">Municipio / Provincia *</Label>
            <Input
              id="ploc"
              data-testid="project-location-input"
              placeholder="Ej. Pozuelo de Alarcón, Madrid"
              value={form.location}
              onChange={set("location")}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="pbud">Presupuesto estimado (€)</Label>
              <Input
                id="pbud"
                data-testid="project-budget-input"
                type="number"
                placeholder="350000"
                value={form.budget}
                onChange={set("budget")}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="psurf">Superficie (m²)</Label>
              <Input
                id="psurf"
                data-testid="project-surface-input"
                type="number"
                placeholder="180"
                value={form.surface}
                onChange={set("surface")}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pdesc">Descripción</Label>
            <Textarea
              id="pdesc"
              data-testid="project-description-input"
              placeholder="Una casa de dos plantas con jardín, luminosa y eficiente…"
              rows={3}
              value={form.description}
              onChange={set("description")}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pstatus">Estado del proyecto</Label>
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger id="pstatus" data-testid="project-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROJECT_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value} data-testid={`project-status-option-${s.value}`}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            data-testid="create-project-button"
            onClick={submit}
            disabled={saving}
            className="w-full rounded-full h-11"
          >
            {saving ? "Creando…" : "Crear proyecto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
